import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import BrandMarkLogo from 'src/components/BrandMarkLogo/BrandMarkLogo'
import SortableTableHeader, {
  SortableTableHeaderAlignment,
} from 'src/components/SortableTableHeader/SortableTableHeader'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, ExtendCurrency } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardRevisionListModel } from 'src/models/virtualCardRevision.models'
import { css } from 'styled-components'

interface VirtualCardRevisionsTableProps {
  className?: string
  revisions: VirtualCardRevisionListModel[]
  timezone: string
  userId: string
  currency: ExtendCurrency
}

const VirtualCardRevisionsTable: React.FC<VirtualCardRevisionsTableProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        display: table;
        width: 100%;

        ${getBreakpointDown(breakpointSizes.XSM)(css`
          display: flex;
          flex-direction: column;
        `)}
      `}
    >
      <div
        css={css`
          display: table-row;
          padding: 0px 40px;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            display: none;
          `)}
        `}
      >
        <SortableTableHeader
          css={css`
            display: table-cell;
            border-bottom: 1px solid ${styleConstants.colors.gray_light};
          `}
          alignment={SortableTableHeaderAlignment.LeftAligned}
          label={translate('Description')}
        />
        <SortableTableHeader
          css={css`
            display: table-cell;
            border-bottom: 1px solid ${styleConstants.colors.gray_light};
          `}
          alignment={SortableTableHeaderAlignment.RightAligned}
          label={translate('Date & Time')}
        />
        <div />
      </div>
      {props.revisions.map((revision, i) => (
        <div
          key={i}
          css={css`
            position: relative;
            display: table-row;
            padding: 0px 40px;
            height: 100%;

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              padding: 20px 20px;
              width: 100%;
              border-bottom: 1px solid ${styleConstants.colors.gray_light};
            `)}
          `}
        >
          <div
            css={css`
              display: table-cell;
              vertical-align: middle;
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              border-bottom: 1px solid ${styleConstants.colors.gray_light};
              display: flex;

              ${getBreakpointUp(breakpointSizes.SM)(css`
                padding: 24px 48px 24px 0px;
                &:first-child {
                  padding: 24px 48px 24px 40px;
                }
                &:last-child {
                  padding: 24px 40px 24px 0px;
                }
              `)};

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: 0px;
                border-bottom: none;
              `)};
            `}
          >
            <div
              css={css`
                display: flex;
              `}
            >
              {revision.operator === undefined && (
                <div
                  css={css`
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: $color-white;
                    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.15);
                  `}
                >
                  <BrandMarkLogo
                    css={css`
                      width: 20px;
                      height: 20px;
                    `}
                  />
                </div>
              )}
              {revision.operator !== undefined && (
                <UserAvatar
                  size={AvatarSizes.Medium}
                  user={revision.operator}
                />
              )}
              <div
                css={css`
                  margin-left: 24px;
                  display: flex;
                  flex-direction: column;
                `}
              >
                {revision
                  .fieldChangeGroups(
                    props.userId,
                    props.timezone,
                    props.currency,
                  )
                  .map((fieldChangeGroup, i) => (
                    <div
                      key={i}
                      css={css`
                        display: flex;
                        flex-direction: column;
                        ${revision.fieldChangeGroups.length > 1 && i > 0
                          ? `margin-top: 10px;`
                          : ``}
                      `}
                    >
                      <span
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                          margin-bottom: 6px;
                        `}
                      >
                        {fieldChangeGroup.label}
                      </span>
                      {fieldChangeGroup.changes.map((change, i) =>
                        change.message !== undefined ? (
                          <div
                            key={i}
                            css={css`
                              ${getFontCss(
                                12,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                          >
                            {change.message}
                          </div>
                        ) : (
                          <div
                            key={i}
                            css={css`
                              ${getFontCss(
                                12,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                          >
                            <span>{change.label}</span>
                            <span
                              css={css`
                                ${getFontCss(
                                  12,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.medium,
                                )};
                              `}
                            >
                              {change.value}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  ))}
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                    margin-top: 12px;

                    ${getBreakpointUp(breakpointSizes.SM)(css`
                      display: none;
                    `)}
                  `}
                >
                  {revision.dateTimeLabel}
                </span>
              </div>
            </div>
          </div>
          <div
            css={css`
              display: table-cell;
              text-align: right;
              height: 100%;
              vertical-align: top;

              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              border-bottom: 1px solid ${styleConstants.colors.gray_light};

              ${getBreakpointUp(breakpointSizes.SM)(css`
                padding: 24px 48px 24px 0px;
                &:first-child {
                  padding: 24px 48px 24px 40px;
                }
                &:last-child {
                  padding: 24px 40px 24px 0px;
                }
              `)};

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: 0px;
                border-bottom: none;
                display: none;
              `)};
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <span>{revision.dateLabel}</span>
              <span>{revision.timeLabel}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default VirtualCardRevisionsTable
